import type { MigrationScope } from '@/lib/migration/scope'
import type { PlanData } from '@/lib/migration/usePlan'
import type { MigrationSummary } from '@/lib/migration/summary'

// ── CSA Submission helpers (Iteration 3 §6–§7) ──────────────────────────────

const EDIT_EVENTS = new Set(['plan_updated', 'annex_added', 'annex_removed', 'annex_archived', 'annex_restored', 'annex_reordered', 'annex_marked_complete', 'asset_added', 'asset_removed', 'field_overridden', 'field_reverted'])

// Last edit to the live plan: when and by whom (status banner), from the
// backend's event log; falls back to the newest updated_at.
export function lastEdit(data: PlanData): { at: string; by: string } | null {
  const ev = [...data.events].filter((e) => EDIT_EVENTS.has(e.event_type) && e.actor !== 'system').sort((a, b) => a.occurred_at.localeCompare(b.occurred_at)).pop()
  const touched = [data.plan?.updated_at, ...data.annexes.map((a) => a.updated_at), ...data.annexes.flatMap((a) => a.blocks.map((b) => b.updated_at))].filter((x): x is string => !!x).sort().pop() ?? null
  if (ev && (!touched || ev.occurred_at >= touched.slice(0, 19))) return { at: ev.occurred_at, by: ev.actor }
  return touched ? { at: touched, by: ev?.actor ?? data.plan?.plan_state.started_by ?? 'system' } : null
}

export interface ChecklistItem {
  key: 'part_a' | 'annexes' | 'items' | 'rescans' | 'date'
  label: string
  ok: boolean
  detail: string
}

// "Before you mark as submitted" (§6.2). Open checks never block; they are
// recorded with the submission (§7.1).
export function submissionChecklist(summary: MigrationSummary, data: PlanData, scope: MigrationScope | null): ChecklistItem[] {
  const items = scope?.items ?? []
  const incomplete = items.filter((i) => i.status !== 'closed' && (!i.owner_user_id || !i.target_date))
  const confirmed = !!data.plan?.plan_state.submission_date_confirmed
  return [
    { key: 'part_a', label: 'Part A complete', ok: summary.part_a.answered === 6, detail: `${summary.part_a.answered} / 6 sections answered` },
    { key: 'annexes', label: 'Annexes present for every CII system', ok: summary.missing_systems.length === 0, detail: summary.missing_systems.length ? `${summary.missing_systems.length} CII ${summary.missing_systems.length === 1 ? 'system' : 'systems'} without an annex` : `${summary.cii_covered} / ${summary.cii_count} CII systems covered` },
    { key: 'items', label: 'Migration items have an owner and a target', ok: incomplete.length === 0, detail: incomplete.length ? `${incomplete.length} without owner or target` : `${items.length} items` },
    { key: 'rescans', label: 'Re-scans clear', ok: summary.items.awaiting_rescan === 0, detail: summary.items.awaiting_rescan ? `${summary.items.awaiting_rescan} verified, awaiting a clear re-scan` : 'no item awaiting a re-scan' },
    { key: 'date', label: 'Submission date confirmed', ok: confirmed, detail: data.plan?.plan_state.submission_date ?? 'not set' },
  ]
}

export interface IncompleteSection {
  key: string
  text: string
  // Where the person fixes it, as a plan link query (part=a / annex=…).
  to: { part: 'a'; section?: string } | { annex: string }
}

// What is still incomplete when a person marks the plan submitted: every
// blocking check the export would refuse, then each annex's open scored
// fields not already named by a check. Shown as a warning; never blocking.
export function incompleteSections(summary: MigrationSummary): IncompleteSection[] {
  const fromChecks: IncompleteSection[] = summary.checks.blocking.map((c) => ({
    key: c.code,
    text: c.text,
    to: c.fix.annex_id ? { annex: c.fix.annex_id } : { part: 'a', section: c.fix.section },
  }))
  const checked = new Set(summary.checks.blocking.map((c) => c.fix.annex_id).filter(Boolean))
  const fromAnnexes: IncompleteSection[] = summary.annex_rows
    .filter((r) => r.completeness.open.length > 0 && !checked.has(r.annex.id))
    .map((r) => ({ key: `open-${r.annex.id}`, text: `${r.annex.system_name || 'Untitled annex'}: ${r.completeness.open.join(', ')} open`, to: { annex: r.annex.id } }))
  return [...fromChecks, ...fromAnnexes]
}
